import BaseComponent from '@utils/base.js';
import tag from '@utils/utilTags.js';

import * as styles from './timer.module.scss';

/** @extends {BaseComponent<HTMLDivElement>} */
export class Timer extends BaseComponent {
  /** @type {BaseComponent<HTMLSpanElement>} */
  minutes;
  /** @type {BaseComponent<HTMLSpanElement>} */
  colon;
  /** @type {BaseComponent<HTMLSpanElement>} */
  seconds;
  /** @type {Animation} */
  animation;
  /**
   * @type {number}
   * @private
   */
  timeSec;
  /** @type {NodeJS.Timeout | number | null} */
  timerId;
  constructor() {
    super({ classes: [styles.timer] });
    this.timeSec = 0;
    this.timerId = null;
    this.createTimer();
  }

  /**
   * Starts the timer.
   * @returns {void}
   */
  startTimer() {
    if (!this.timerId) {
      this.timerId = setInterval(() => {
        this.timeSec++;
        this.updateDisplay();
      }, 1000);

      this.makeActive();
      this.updateDisplay();

      this.animation.play();
    }
  }

  /**
   * Returns current counter.
   * @returns {number}
   */
  get time() {
    return this.timeSec;
  }

  /**
   * Pauses the timer.
   * @returns {void}
   */
  pauseTimer() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
      this.animation.cancel();
    }
  }

  /**
   * Resets the timer.
   * @param {number} [newTime]
   * @returns {void}
   */
  resetTimer(newTime) {
    this.pauseTimer();
    this.timeSec = newTime || 0;
    this.updateDisplay();
    this.removeClasses(styles.active);

    if (newTime === undefined) {
      this.minutes.setText('');
      this.colon.setText('');
      this.seconds.setText('');
    } else {
      this.makeActive();
    }
  }

  /**
   * Makes the timer visually active.
   * @returns {void}
   */
  makeActive() {
    this.colon.setText(':');
    this.addClasses(styles.active);
  }

  /**
   * Updates the time.
   * @returns {void}
   * @private
   */
  updateDisplay() {
    const min = Math.floor(this.timeSec / 60) % 60;
    const sec = this.timeSec % 60;
    this.minutes.setText(`${min < 10 ? '0' : ''}${min}`);
    this.seconds.setText(`${sec < 10 ? '0' : ''}${sec}`);
  }

  /**
   * Creates the timer component.
   * @returns {void}
   * @private
   */
  createTimer() {
    const background = tag.span({
      classes: [styles.background],
      text: '88:88',
    });

    this.minutes = tag.span();
    this.colon = tag.span();
    this.seconds = tag.span();

    const container = tag.span(
      { classes: [styles.container] },
      this.minutes,
      this.colon,
      this.seconds
    );

    this.appendChildren(container, background);

    this.animation = new Animation(
      new KeyframeEffect(
        this.colon.getElement(),
        [
          { color: 'inherit', offset: 0 },
          { color: 'inherit', offset: 0.49 },
          { color: 'transparent', offset: 0.5 },
          { color: 'transparent', offset: 0.99 },
          { color: 'inherit', offset: 1 },
        ],
        { duration: 2000, iterations: Infinity }
      ),
      document.timeline
    );
  }
}

export default new Timer();
